import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getMqttProxyWsUrl } from '@/lib/api'
import type {
  ConnectionStatus,
  MqttMessage,
  MqttProxyConnectionConfig,
  MqttQoS,
  ProxyResponse,
  SUBSCRIPTION_COLORS,
  Subscription,
} from '@/lib/mqtt-types'

const SUBSCRIPTION_COLORS_ARRAY = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#F97316',
]

const DEFAULT_MAX_MESSAGES = 1000
const MESSAGE_BATCH_INTERVAL = 100 // Batch message updates every 100ms

interface UseMqttProxyOptions {
  maxMessages?: number
  onConnect?: (clientId: string) => void
  onDisconnect?: (reason?: string) => void
  onError?: (error: string, details?: string) => void
  onMessage?: (message: MqttMessage) => void
  onSubscribed?: (topic: string, qos: number) => void
  onUnsubscribed?: (topic: string) => void
  onPublished?: (topic: string) => void
  onSubscribeError?: (topic: string, error: string) => void
  onPublishError?: (topic: string, error: string) => void
}

export function useMqttProxy(options: UseMqttProxyOptions = {}) {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const [clientId, setClientId] = useState<string | null>(null)
  const [messages, setMessages] = useState<MqttMessage[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isPaused, setIsPaused] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const colorIndexRef = useRef(0)
  const currentConfigRef = useRef<MqttProxyConnectionConfig | null>(null)
  const statusRef = useRef<ConnectionStatus>('disconnected')
  const isPausedRef = useRef(false)
  const pendingSubscriptionsRef = useRef<Array<{ topic: string; qos: MqttQoS }>>([])
  const maxMessagesRef = useRef(options.maxMessages ?? DEFAULT_MAX_MESSAGES)
  const mountedRef = useRef(true)
  const optionsRef = useRef(options)

  // Message batching for high throughput
  const pendingMessagesRef = useRef<MqttMessage[]>([])
  const pendingCountUpdatesRef = useRef<Map<string, number>>(new Map())
  const batchTimerRef = useRef<NodeJS.Timeout | null>(null)
  const subscriptionsRef = useRef<Subscription[]>([])

  // Keep optionsRef in sync with options (prevents stale closures)
  useEffect(() => {
    optionsRef.current = options
  }, [options])

  // Keep maxMessagesRef in sync with options
  useEffect(() => {
    maxMessagesRef.current = options.maxMessages ?? DEFAULT_MAX_MESSAGES
  }, [options.maxMessages])

  // Keep statusRef in sync with status
  useEffect(() => {
    statusRef.current = status
  }, [status])

  // Keep isPausedRef in sync with isPaused
  useEffect(() => {
    isPausedRef.current = isPaused
  }, [isPaused])

  // Keep subscriptionsRef in sync with subscriptions
  useEffect(() => {
    subscriptionsRef.current = subscriptions
  }, [subscriptions])

  // Safe state setters that check if mounted
  const safeSetStatus = useCallback(
    (value: ConnectionStatus | ((prev: ConnectionStatus) => ConnectionStatus)) => {
      if (mountedRef.current) setStatus(value)
    },
    [],
  )

  const safeSetClientId = useCallback((value: string | null) => {
    if (mountedRef.current) setClientId(value)
  }, [])

  const safeSetMessages = useCallback(
    (value: MqttMessage[] | ((prev: MqttMessage[]) => MqttMessage[])) => {
      if (mountedRef.current) setMessages(value)
    },
    [],
  )

  const safeSetSubscriptions = useCallback(
    (value: Subscription[] | ((prev: Subscription[]) => Subscription[])) => {
      if (mountedRef.current) setSubscriptions(value)
    },
    [],
  )

  const safeSetError = useCallback((value: string | null) => {
    if (mountedRef.current) setError(value)
  }, [])

  // Flush batched messages to state
  const flushPendingMessages = useCallback(() => {
    if (!mountedRef.current) return

    const pendingMessages = pendingMessagesRef.current
    const pendingCounts = pendingCountUpdatesRef.current

    if (pendingMessages.length === 0 && pendingCounts.size === 0) return

    // Clear pending refs first to avoid race conditions
    pendingMessagesRef.current = []
    pendingCountUpdatesRef.current = new Map()

    // Batch update messages
    if (pendingMessages.length > 0) {
      safeSetMessages((prev) => {
        const updated = [...pendingMessages, ...prev]
        return updated.slice(0, maxMessagesRef.current)
      })
    }

    // Batch update subscription counts
    if (pendingCounts.size > 0) {
      safeSetSubscriptions((subs) =>
        subs.map((sub) => {
          const countIncrease = pendingCounts.get(sub.topic) ?? 0
          if (countIncrease > 0) {
            return { ...sub, messageCount: sub.messageCount + countIncrease }
          }
          return sub
        }),
      )
    }
  }, [safeSetMessages, safeSetSubscriptions])

  // Schedule a batched flush
  const scheduleBatchFlush = useCallback(() => {
    if (batchTimerRef.current === null) {
      batchTimerRef.current = setTimeout(() => {
        batchTimerRef.current = null
        flushPendingMessages()
      }, MESSAGE_BATCH_INTERVAL)
    }
  }, [flushPendingMessages])

  const getNextColor = useCallback(() => {
    const color =
      SUBSCRIPTION_COLORS_ARRAY[colorIndexRef.current % SUBSCRIPTION_COLORS_ARRAY.length]
    colorIndexRef.current++
    return color
  }, [])

  const sendMessage = useCallback((message: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
    }
  }, [])

  const handleProxyResponse = useCallback(
    (response: ProxyResponse) => {
      // Use optionsRef to avoid stale closure issues
      const opts = optionsRef.current

      switch (response.type) {
        case 'connected':
          safeSetStatus('connected')
          safeSetClientId(response.clientId)
          safeSetError(null)
          opts.onConnect?.(response.clientId)
          break

        case 'disconnected':
          safeSetStatus('disconnected')
          safeSetClientId(null)
          opts.onDisconnect?.(response.reason)
          break

        case 'subscribed':
          safeSetSubscriptions((subs) =>
            subs.map((sub) =>
              sub.topic === response.topic ? { ...sub, active: true, qos: response.qos } : sub,
            ),
          )
          opts.onSubscribed?.(response.topic, response.qos)
          break

        case 'unsubscribed':
          safeSetSubscriptions((subs) =>
            subs.map((sub) => (sub.topic === response.topic ? { ...sub, active: false } : sub)),
          )
          opts.onUnsubscribed?.(response.topic)
          break

        case 'published':
          opts.onPublished?.(response.topic)
          break

        case 'subscribe_error':
          // Handle subscription error from backend
          safeSetSubscriptions((subs) => subs.filter((sub) => sub.topic !== response.topic))
          opts.onSubscribeError?.(response.topic, response.message || 'Subscription failed')
          break

        case 'publish_error':
          // Handle publish error from backend
          opts.onPublishError?.(response.topic, response.message || 'Publish failed')
          break

        case 'message':
          // Use ref to avoid stale closure issues with isPaused
          if (!isPausedRef.current) {
            try {
              // Parse timestamp safely
              let timestamp: Date
              try {
                timestamp = new Date(response.timestamp)
                if (isNaN(timestamp.getTime())) {
                  timestamp = new Date()
                }
              } catch {
                timestamp = new Date()
              }

              const newMessage: MqttMessage = {
                id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                topic: response.topic,
                payload: response.payload ?? '',
                qos: response.qos,
                retain: response.retain,
                timestamp,
                format: detectPayloadFormat(response.payload ?? ''),
                userProperties: response.userProperties,
              }

              // Add to pending messages batch instead of updating state directly
              // Limit pending buffer to prevent memory issues
              if (pendingMessagesRef.current.length < maxMessagesRef.current) {
                pendingMessagesRef.current.push(newMessage)
              }

              // Update pending subscription counts using ref (no state update)
              for (const sub of subscriptionsRef.current) {
                if (topicMatches(sub.topic, response.topic)) {
                  const currentCount = pendingCountUpdatesRef.current.get(sub.topic) ?? 0
                  pendingCountUpdatesRef.current.set(sub.topic, currentCount + 1)
                }
              }

              // Schedule a batched flush
              scheduleBatchFlush()

              opts.onMessage?.(newMessage)
            } catch (e) {
              console.error('Error processing MQTT message:', e)
            }
          }
          break

        case 'error': {
          const errorMessage = response.message || 'Unknown error occurred'
          const config = currentConfigRef.current
          const details = config ? `${config.protocol}://${config.host}:${config.port}` : undefined
          safeSetError(errorMessage)
          opts.onError?.(errorMessage, details)
          // If error occurs during connection, reset status (use statusRef to avoid stale closure)
          if (statusRef.current === 'connecting') {
            safeSetStatus('disconnected')
          }
          break
        }
      }
    },
    [safeSetStatus, safeSetClientId, safeSetError, safeSetSubscriptions, scheduleBatchFlush],
  )

  const connect = useCallback(
    (config: MqttProxyConnectionConfig) => {
      // Close existing connection and clean up handlers
      if (wsRef.current) {
        const oldWs = wsRef.current
        oldWs.onopen = null
        oldWs.onmessage = null
        oldWs.onerror = null
        oldWs.onclose = null
        oldWs.close()
        wsRef.current = null
      }

      // Store config for error reporting
      currentConfigRef.current = config

      safeSetStatus('connecting')
      safeSetError(null)

      const wsUrl = getMqttProxyWsUrl()
      let ws: WebSocket

      try {
        ws = new WebSocket(wsUrl)
      } catch (e) {
        const errorMsg = 'Failed to create WebSocket connection to backend proxy'
        safeSetStatus('disconnected')
        safeSetError(errorMsg)
        optionsRef.current.onError?.(errorMsg, `Backend URL: ${wsUrl}`)
        return
      }

      wsRef.current = ws

      ws.onopen = () => {
        // Check if this is still the current WebSocket
        if (wsRef.current !== ws) return
        // Send connect message
        sendMessage({
          type: 'connect',
          config,
        })
      }

      ws.onmessage = (event) => {
        // Check if this is still the current WebSocket
        if (wsRef.current !== ws) return
        try {
          const response = JSON.parse(event.data) as ProxyResponse
          handleProxyResponse(response)
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e)
          const errorMsg = 'Failed to parse response from MQTT proxy'
          safeSetError(errorMsg)
          optionsRef.current.onError?.(errorMsg, String(e))
        }
      }

      ws.onerror = () => {
        // Check if this is still the current WebSocket
        if (wsRef.current !== ws) return
        const errorMsg = 'WebSocket connection error'
        const details = `Failed to connect to backend proxy at ${wsUrl}. Please ensure the backend server is running.`
        safeSetStatus('disconnected') // Use disconnected so form fields are re-enabled for retry
        safeSetError(errorMsg)
        optionsRef.current.onError?.(errorMsg, details)
      }

      ws.onclose = (event) => {
        // Check if this is still the current WebSocket
        if (wsRef.current !== ws) return
        // Use statusRef to avoid stale closure issues
        if (statusRef.current === 'connected' || statusRef.current === 'connecting') {
          safeSetStatus('disconnected')
          safeSetClientId(null)

          // Provide more context for unexpected disconnections
          if (event.code !== 1000 && event.code !== 1001) {
            const reason = event.reason || getCloseCodeReason(event.code)
            optionsRef.current.onDisconnect?.(`Connection closed: ${reason} (code: ${event.code})`)
          }
        }
        currentConfigRef.current = null
      }
    },
    [sendMessage, handleProxyResponse, safeSetStatus, safeSetError, safeSetClientId],
  )

  const disconnect = useCallback(() => {
    // Flush any pending messages before disconnecting
    if (batchTimerRef.current) {
      clearTimeout(batchTimerRef.current)
      batchTimerRef.current = null
    }
    flushPendingMessages()

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      sendMessage({ type: 'disconnect' })
    }
    // Clean up WebSocket handlers before closing
    if (wsRef.current) {
      const ws = wsRef.current
      ws.onopen = null
      ws.onmessage = null
      ws.onerror = null
      ws.onclose = null
      ws.close()
      wsRef.current = null
    }
    safeSetStatus('disconnected')
    safeSetClientId(null)
    safeSetSubscriptions((subs) => subs.map((sub) => ({ ...sub, active: false })))
  }, [sendMessage, safeSetStatus, safeSetClientId, safeSetSubscriptions, flushPendingMessages])

  const subscribe = useCallback(
    (topic: string, qos: MqttQoS = 0) => {
      // Check if already subscribed
      const existing = subscriptions.find((s) => s.topic === topic)
      if (existing?.active) return

      // Add to subscriptions list if not exists
      if (!existing) {
        safeSetSubscriptions((subs) => [
          ...subs,
          {
            topic,
            qos,
            color: getNextColor(),
            active: false,
            messageCount: 0,
          },
        ])
      }

      // If connected, send subscribe message immediately (use statusRef to avoid stale closure)
      if (statusRef.current === 'connected') {
        sendMessage({
          type: 'subscribe',
          topic,
          qos,
        })
      } else {
        // Store as pending subscription to be sent when connected
        pendingSubscriptionsRef.current.push({ topic, qos })
      }
    },
    [subscriptions, sendMessage, getNextColor, safeSetSubscriptions],
  )

  const unsubscribe = useCallback(
    (topic: string) => {
      // Use statusRef to avoid stale closure
      if (statusRef.current !== 'connected') return

      sendMessage({
        type: 'unsubscribe',
        topic,
      })
    },
    [sendMessage],
  )

  const removeSubscription = useCallback(
    (topic: string) => {
      // First unsubscribe if active
      const sub = subscriptions.find((s) => s.topic === topic)
      if (sub?.active) {
        unsubscribe(topic)
      }

      // Remove from list
      safeSetSubscriptions((subs) => subs.filter((s) => s.topic !== topic))
    },
    [subscriptions, unsubscribe, safeSetSubscriptions],
  )

  const publish = useCallback(
    (
      topic: string,
      payload: string,
      qos: MqttQoS = 0,
      retain = false,
      userProperties?: Record<string, string>,
    ) => {
      // Use statusRef to avoid stale closure
      if (statusRef.current !== 'connected') return

      sendMessage({
        type: 'publish',
        topic,
        payload,
        qos,
        retain,
        userProperties,
      })
    },
    [sendMessage],
  )

  const clearMessages = useCallback(() => {
    safeSetMessages([])
    safeSetSubscriptions((subs) => subs.map((sub) => ({ ...sub, messageCount: 0 })))
  }, [safeSetMessages, safeSetSubscriptions])

  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev)
  }, [])

  // Subscribe pending subscriptions when connected
  useEffect(() => {
    if (status === 'connected' && pendingSubscriptionsRef.current.length > 0) {
      // Send subscribe messages for all pending subscriptions
      const pending = [...pendingSubscriptionsRef.current]
      pendingSubscriptionsRef.current = []

      pending.forEach(({ topic, qos }) => {
        sendMessage({
          type: 'subscribe',
          topic,
          qos,
        })
      })
    }
  }, [status, sendMessage])

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      // Clear batch timer
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current)
        batchTimerRef.current = null
      }
      // Clear pending batches
      pendingMessagesRef.current = []
      pendingCountUpdatesRef.current = new Map()
      // Clean up WebSocket handlers to prevent memory leaks
      if (wsRef.current) {
        const ws = wsRef.current
        ws.onopen = null
        ws.onmessage = null
        ws.onerror = null
        ws.onclose = null
        ws.close()
        wsRef.current = null
      }
    }
  }, [])

  const setMaxMessages = useCallback(
    (max: number) => {
      maxMessagesRef.current = max
      // Trim existing messages if needed
      safeSetMessages((prev) => prev.slice(0, max))
    },
    [safeSetMessages],
  )

  return {
    status,
    clientId,
    messages,
    subscriptions,
    error,
    isPaused,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    removeSubscription,
    publish,
    clearMessages,
    togglePause,
    setMaxMessages,
  }
}

// Helper function to detect payload format
function detectPayloadFormat(payload: string): MqttMessage['format'] {
  try {
    if (!payload || typeof payload !== 'string') {
      return 'text'
    }

    const trimmed = payload.trim()
    if (!trimmed) {
      return 'text'
    }

    // Try JSON (check for common JSON starting characters first for performance)
    if (
      trimmed.startsWith('{') ||
      trimmed.startsWith('[') ||
      trimmed === 'null' ||
      trimmed === 'true' ||
      trimmed === 'false'
    ) {
      try {
        JSON.parse(trimmed)
        return 'json'
      } catch {
        // Not JSON
      }
    }

    // Try XML
    if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
      return 'xml'
    }

    // Try Hex (check if all chars are hex and even length, but skip if too long to avoid performance issues)
    if (trimmed.length <= 1000 && /^[0-9a-fA-F]+$/.test(trimmed) && trimmed.length % 2 === 0) {
      return 'hex'
    }

    // Try Base64 (skip if too long to avoid performance issues)
    if (trimmed.length <= 1000 && /^[A-Za-z0-9+/]+=*$/.test(trimmed) && trimmed.length % 4 === 0) {
      try {
        const decoded = atob(trimmed)
        const reencoded = btoa(decoded)
        if (reencoded === trimmed) {
          return 'base64'
        }
      } catch {
        // Not valid Base64
      }
    }

    return 'text'
  } catch (e) {
    console.error('Error detecting payload format:', e)
    return 'text'
  }
}

// Helper function to check if a topic matches a subscription pattern
function topicMatches(pattern: string, topic: string): boolean {
  const patternParts = pattern.split('/')
  const topicParts = topic.split('/')

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i]
    const topicPart = topicParts[i]

    if (patternPart === '#') {
      return true // Multi-level wildcard matches everything after
    }

    if (patternPart === '+') {
      continue // Single-level wildcard matches any single level
    }

    if (patternPart !== topicPart) {
      return false
    }
  }

  return patternParts.length === topicParts.length
}

// Helper function to get human-readable WebSocket close reasons
function getCloseCodeReason(code: number): string {
  const reasons: Record<number, string> = {
    1000: 'Normal closure',
    1001: 'Going away',
    1002: 'Protocol error',
    1003: 'Unsupported data',
    1005: 'No status received',
    1006: 'Abnormal closure (connection lost)',
    1007: 'Invalid frame payload data',
    1008: 'Policy violation',
    1009: 'Message too big',
    1010: 'Missing extension',
    1011: 'Internal server error',
    1012: 'Service restart',
    1013: 'Try again later',
    1014: 'Bad gateway',
    1015: 'TLS handshake failure',
  }
  return reasons[code] || `Unknown (code: ${code})`
}

export default useMqttProxy
